import { Play, ShieldAlert } from 'lucide-react'
import { useEffect } from 'react'
import { Link } from '@tanstack/react-router'

import {
  useChannelScheduleHistory,
  useSimulateUpload,
  type SimulateOut,
} from '@/api/endpoints/scheduler'
import { Button } from '@/components/ui/Button'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import { DialogContent, DialogRoot } from '@/components/ui/Dialog'
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/Tabs'
import { ScoreGauge } from '@/components/ds/ScoreGauge'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import { preservingChannel } from '@/lib/routing'
import styles from './ChannelDetailsDialog.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

export interface ChannelDetailsDialogProps {
  open: boolean
  onClose: () => void
  slug: string
  channelName?: string
}

/**
 * Side panel for a channel in the Scheduler. Two tabs:
 *   - Histórico: recent cron runs from /scheduler/channels/{slug}/history
 *   - Próximo upload: dry-run of the upload cycle, showing what would
 *     publish and what's quarantined right now.
 *
 * The simulate call is on-demand (mutation pattern) so opening the dialog
 * doesn't always fire the ranking engine — users pay that cost only when
 * they switch to the "Próximo upload" tab.
 */
export function ChannelDetailsDialog({
  open,
  onClose,
  slug,
  channelName,
}: ChannelDetailsDialogProps) {
  return (
    <DialogRoot
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={`Canal · ${channelName ?? slug}`}
        description="Histórico de execuções do cron + preview da fila de upload."
        className={styles.dialog}
      >
        <TabsRoot defaultValue="history" persistKey="channel-details">
          <TabsList>
            <TabsTrigger value="history">Histórico</TabsTrigger>
            <TabsTrigger value="simulate">Próximo upload</TabsTrigger>
          </TabsList>
          <TabsContent value="history">
            <HistoryPanel slug={slug} />
          </TabsContent>
          <TabsContent value="simulate">
            <SimulatePanel slug={slug} open={open} />
          </TabsContent>
        </TabsRoot>
      </DialogContent>
    </DialogRoot>
  )
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

function HistoryPanel({ slug }: { slug: string }) {
  const { data, isPending, isError, error, refetch } = useChannelScheduleHistory(slug, 7)

  if (isPending) return <div className={styles.loading}>Carregando histórico...</div>
  if (isError) {
    return (
      <QueryErrorNotice
        error={error}
        label="o histórico"
        onRetry={() => {
          void refetch()
        }}
      />
    )
  }

  if (data.length === 0) {
    return <div className={styles.empty}>Nenhuma execução nos últimos 7 dias.</div>
  }

  return (
    <div className={styles.body}>
      <div className={styles.historyHead}>
        <div className={styles.historyRow}>
          <span>Quando</span>
          <span>Ciclo</span>
          <span>Status</span>
          <span>Detalhe</span>
          <span>Vídeos</span>
        </div>
      </div>
      <div className={styles.historyList}>
        {data.map((run, idx) => (
          <HistoryRow key={historyKey(run, idx)} run={run} />
        ))}
      </div>
    </div>
  )
}

interface HistoryRun {
  started_at?: string
  ended_at?: string
  run_date?: string
  cycle?: string
  status?: string
  triggered_by?: string
  error_summary?: string
  videos?: unknown[]
  videos_requested?: number
  videos_completed?: number
}

function HistoryRow({ run }: { run: Record<string, unknown> }) {
  // Legacy rows live alongside new ones; field set varies. Read defensively.
  const r = run as unknown as HistoryRun
  const when = r.started_at ?? r.run_date ?? '—'
  const cycle = r.cycle ?? '—'
  const status = r.status ?? '—'
  const triggered = r.triggered_by ? ` · ${r.triggered_by}` : ''
  const errorSummary = r.error_summary ?? ''
  const videosCount = Array.isArray(r.videos)
    ? r.videos.length
    : (r.videos_completed ?? 0)

  return (
    <div className={styles.historyRow}>
      <span className={styles.historyCell}>{formatWhen(when)}</span>
      <Chip>{cycle}</Chip>
      <Chip variant={chipForRunStatus(status)}>{status}</Chip>
      <span className={styles.historyCellMuted} title={errorSummary || undefined}>
        {errorSummary || `agendado${triggered}`}
      </span>
      <span className={styles.historyCell}>{String(videosCount)}</span>
    </div>
  )
}

function historyKey(run: Record<string, unknown>, idx: number): string {
  const started = run['started_at']
  const safe = typeof started === 'string' ? started : String(idx)
  return `${safe}-${String(idx)}`
}

function chipForRunStatus(status: string): ChipVariant {
  if (status === 'completed') return 'success'
  if (status === 'partial') return 'warning'
  if (status === 'failed') return 'danger'
  if (status === 'running') return 'info'
  return 'default'
}

// ---------------------------------------------------------------------------
// Simulate tab
// ---------------------------------------------------------------------------

function SimulatePanel({ slug, open }: { slug: string; open: boolean }) {
  const mutation = useSimulateUpload()

  // Auto-trigger the first time the user opens the dialog with this slug.
  // Subsequent re-renders (same slug, still open) skip — mutate only again
  // when they click "Re-simular".
  useEffect(() => {
    if (!open) {
      mutation.reset()
      return
    }
    if (!mutation.isPending && mutation.data === undefined && !mutation.isError) {
      mutation.mutate(slug)
    }
    // Intentional: controlling the trigger precisely by open/slug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slug])

  if (mutation.isPending) {
    return <div className={styles.loading}>Calculando ranking atual...</div>
  }
  if (mutation.isError) {
    return (
      <div className={styles.empty}>
        <p>{errorMessage(mutation.error)}</p>
        <Button
          size="sm"
          leadingIcon={<Play />}
          onClick={() => {
            mutation.mutate(slug)
          }}
        >
          Tentar de novo
        </Button>
      </div>
    )
  }
  if (!mutation.data) {
    return <div className={styles.empty}>Clique em simular pra calcular.</div>
  }

  const sim = mutation.data
  const ordered = sim.ordered
  const quarantined = sim.quarantined
  const total = ordered.length + quarantined.length

  return (
    <div className={styles.body}>
      <div className={styles.simulateBar}>
        <span className={styles.simulateMeta}>
          {total} vídeo{total === 1 ? '' : 's'} no escopo · {ordered.length} elegíveis
          {quarantined.length > 0 ? ` · ${String(quarantined.length)} em quarentena` : ''}
        </span>
        <Button
          size="sm"
          variant="ghost"
          leadingIcon={<Play />}
          onClick={() => {
            mutation.mutate(slug)
          }}
        >
          Re-simular
        </Button>
      </div>

      {ordered.length > 0 ? (
        <>
          <span className={styles.sectionLabel}>Fila · ordenada</span>
          <div className={styles.simulateList}>
            {ordered.map((item, idx) => (
              <SimulateRow key={`ord-${String(idx)}`} item={item} rank={idx + 1} />
            ))}
          </div>
        </>
      ) : (
        <div className={styles.empty}>Nenhum vídeo elegível.</div>
      )}

      {quarantined.length > 0 ? (
        <>
          <span className={styles.sectionLabel}>Em quarentena</span>
          <div className={styles.simulateList}>
            {quarantined.map((item, idx) => (
              <SimulateRow key={`qrt-${String(idx)}`} item={item} quarantined />
            ))}
          </div>
        </>
      ) : null}

      <ConfigRow config={sim.config_used} />
    </div>
  )
}

interface SimulateRowItem {
  id?: string
  language?: string
  factory?: string
  final_score?: number | null
}

interface SimulateRowProps {
  item: SimulateOut['ordered'][number]
  rank?: number
  quarantined?: boolean
}

function SimulateRow({ item, rank, quarantined }: SimulateRowProps) {
  const r = item as unknown as SimulateRowItem
  const id = r.id ?? ''
  const lang = r.language ?? 'pt-br'
  const factory = r.factory ?? ''
  const score = r.final_score ?? null

  return (
    <div className={cn(styles.simulateRow, quarantined && styles.quarantined)}>
      {quarantined ? (
        <ShieldAlert aria-hidden style={{ color: 'var(--danger)', width: 22, height: 22 }} />
      ) : (
        <ScoreGauge score={score} size={40} />
      )}
      <div>
        <Link
          to="/projects/$projectId"
          params={{ projectId: id }}
          search={preservingChannel({ lang })}
          className={styles.simulateTitle}
        >
          {formatRank(rank)}
          {id || '—'}
        </Link>
        <div className={styles.simulateSub}>
          {factory} · {lang}
        </div>
      </div>
      <Chip variant={quarantined ? 'danger' : 'default'}>
        {quarantined ? 'bloqueado' : 'elegível'}
      </Chip>
    </div>
  )
}

function formatRank(rank: number | undefined): string {
  if (rank === undefined) return '#'
  return `#${String(rank).padStart(2, '0')} · `
}

function ConfigRow({ config }: { config: SimulateOut['config_used'] }) {
  const entries = Object.entries(config).filter(([, v]) => v !== null && v !== undefined)
  if (entries.length === 0) return null
  return (
    <div className={styles.configRow}>
      {entries.map(([k, v]) => (
        <span key={k} className={styles.configChip}>
          {k}: <strong>{String(v)}</strong>
        </span>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatWhen(iso: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}
